// ✅ SupervisorPaymentEntry.jsx (UPDATED WITH localStorage helper + commonHeaders)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

const SupervisorPaymentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Edit mode input from listing
  const paymentid = location?.state?.paymentid || null;
  const editflag = Boolean(location?.state?.editflag);

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "HO";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ keep loginuserid for body also if backend expects it
  const loginuserid = userId || getLS("loginuserid");

  // ----------------------------
  // Dropdown
  // ----------------------------
  const [supervisors, setSupervisors] = useState([]);

  // ----------------------------
  // Form
  // ----------------------------
  const [formData, setFormData] = useState({
    date: "",
    entryNo: "",
    supervisorid: "",
    address: "",
    paymentMode: "Bank",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    bankBranchName: "",
    lastAmount: "",
    cashInHand: "",
    amount: "",
    reason: "",
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ----------------------------
  // Helpers
  // ----------------------------
  const ddmmyyyyToYmd = (s) => {
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split("/");
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  // ----------------------------
  // API
  // ----------------------------
  const loadSupervisors = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorEntry/supervisors`, {
        headers: commonHeaders,
      });
      console.log("response:", res.data.data);
      setSupervisors(Array.isArray(res.data.data) ? res.data.data : []);

    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load supervisors");
      setSupervisors([]);
    }
  };

  const loadNextEntryNo = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorEntry/nextPaymentCode`, {
        headers: commonHeaders,
      });
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, entryNo: res.data.entryNo || "" }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load entry number");
    }
  };

  const loadPaymentById = async (pid) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorEntry/getById`, {
        params: { paymentid: pid },
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load record");
        return;
      }

      const d = res.data?.data;
      if (!d) return;

      setFormData((prev) => ({
        ...prev,
        entryNo: d.fld_entryno || "",
        supervisorid: String(d.fld_supervisorid || ""),
        date: ddmmyyyyToYmd(d.fld_date || ""),
        paymentMode: d.fld_paymentmode || "Bank",
        address: d.fld_address1 || "",
        accountNumber: d.fld_accnumber || "",
        ifscCode: d.fld_ifsccode || "",
        bankName: d.fld_bankname || "",
        bankBranchName: d.fld_bankbranchname || "",
        lastAmount: d.fld_lastamount || "",
        cashInHand: d.fld_cashinhand || "",
        amount: d.fld_amount || "",
        reason: d.fld_reason || "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load payment details");
    }
  };

  const loadSupervisorDetails = async (supervisorid) => {
    if (!supervisorid) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/supervisorEntry/supervisorDetails`, {
        params: { supervisorid },
        headers: commonHeaders,
      });

      if (!res.data?.success) return;

      const r = res.data?.data;
      if (!r) return;

      const addressText =
        `${r.fld_address1 || ""}, ${r.fld_districtname || ""}, ${r.fld_statename || ""}, ${r.fld_countryname || ""}` +
        (r.fld_phoneno ? ` - Phone no: ${r.fld_phoneno}` : "");

      setFormData((prev) => ({
        ...prev,
        address: addressText.trim(),
        accountNumber: r.fld_accnumber || "",
        ifscCode: r.fld_ifsccode || "",
        bankName: r.fld_bankname || "",
        bankBranchName: r.fld_bankbranchname || "",
        lastAmount: r.fld_lastamount || "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load supervisor details");
    }
  };

  // ----------------------------
  // Save / Update
  // ----------------------------
  const validate = () => {
    if (!formData.date) return "Date is required";
    if (!formData.entryNo) return "Entry No is required";
    if (!formData.supervisorid) return "Supervisor is required";
    if (formData.amount === "" || Number(formData.amount) <= 0) return "Amount must be > 0";

    if (formData.paymentMode === "Bank") {
      if (!formData.accountNumber) return "Account Number is required for Bank payment";
      if (!formData.bankName) return "Bank Name is required for Bank payment";
    } else {
      if (formData.cashInHand === "") return "Cash In Hand is required for Cash payment";
      if (Number(formData.amount) > Number(formData.cashInHand || 0)) {
        return "Amount cannot exceed Cash In Hand";
      }
    }

    if (formData.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      return "Invalid IFSC code";
    }

    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    try {
      setLoading(true);

      if (editflag && paymentid) {
        const res = await axios.post(
          `${BASE_URL}/api/supervisorEntry/update`,
          {
            paymentid,
            date: formData.date,
            supervisorid: formData.supervisorid,
            paymentMode: formData.paymentMode,
            lastAmount: formData.lastAmount,
            cashInHand: formData.cashInHand,
            amount: formData.amount,
            reason: formData.reason,
            loginuserid,
          },
          {
            headers: commonHeaders,
          }
        );

        if (res.data?.success) {
          toast.success("Updated Successfully");
          navigate("/PaymentVouchers/SupervisorPaymentListing");
        } else {
          toast.error(res.data?.message || "Update failed");
        }
      } else {
        const res = await axios.post(
          `${BASE_URL}/api/supervisorEntry/save`,
          {
            date: formData.date,
            supervisorid: formData.supervisorid,
            paymentMode: formData.paymentMode,
            lastAmount: formData.lastAmount,
            cashInHand: formData.cashInHand,
            amount: formData.amount,
            reason: formData.reason,
            loginuserid,
          },
          {
            headers: commonHeaders,
          }
        );

        if (res.data?.success) {
          toast.success(res.data?.message || "Saved Successfully");
          navigate("/PaymentVouchers/SupervisorPaymentListing");
        } else {
          toast.error(res.data?.message || "Save failed");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/PaymentVouchers/SupervisorPaymentListing");
  };

  // ----------------------------
  // Init
  // ----------------------------
  useEffect(() => {
    loadSupervisors();

    if (editflag && paymentid) {
      loadPaymentById(paymentid);
    } else {
      loadNextEntryNo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formData.supervisorid) return;
    loadSupervisorDetails(formData.supervisorid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.supervisorid]);

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR PAYMENT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />
         
          <button
              onClick={handleBack}
              disabled={loading}
             className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
            >
             <FaArrowLeft/>  Back
            </button>
        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {editflag ? "Edit Supervisor Payment" : "New Supervisor Payment"}
            </h1>

            <div className="text-sm opacity-80">
              {editflag ? `Payment ID: ${paymentid}` : "Create mode"}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Entry No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.entryNo}
                  disabled
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Payment Mode <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMode"
                      value="Bank"
                      checked={formData.paymentMode === "Bank"}
                      onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                      className="mr-2"
                    />
                    Bank
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentMode"
                      value="Cash"
                      checked={formData.paymentMode === "Cash"}
                      onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                      className="mr-2"
                    />
                    Cash
                  </label>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={formData.ifscCode}
                  onChange={(e) => handleInputChange("ifscCode", e.target.value.toUpperCase())}
                  disabled={formData.paymentMode === "Cash"}
                  maxLength={11}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  } ${formData.paymentMode === "Cash" ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Bank Branch Name
                </label>
                <input
                  type="text"
                  value={formData.bankBranchName}
                  onChange={(e) => handleInputChange("bankBranchName", e.target.value)}
                  disabled={formData.paymentMode === "Cash"}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  } ${formData.paymentMode === "Cash" ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Cash In Hand {formData.paymentMode === "Cash" && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  value={formData.cashInHand}
                  onChange={(e) => handleInputChange("cashInHand", e.target.value)}
                  disabled={formData.paymentMode === "Bank"}
                  min="0"
                  step="0.01"
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  } ${formData.paymentMode === "Bank" ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Supervisor Name <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.supervisorid}
                  onChange={(e) => handleInputChange("supervisorid", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {supervisors.map((s) => (
                    <option key={s.fld_supervisorid} value={s.fld_supervisorid}>
                      {s.fld_supervisorname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Account Number {formData.paymentMode === "Bank" && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => handleInputChange("accountNumber", e.target.value)}
                  disabled={formData.paymentMode === "Cash"}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  } ${formData.paymentMode === "Cash" ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Bank Name {formData.paymentMode === "Bank" && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange("bankName", e.target.value)}
                  disabled={formData.paymentMode === "Cash"}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  } ${formData.paymentMode === "Cash" ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Last Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.lastAmount}
                  onChange={(e) => handleInputChange("lastAmount", e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleInputChange("amount", e.target.value)}
                  min="0"
                  step="0.01"
                  className={`w-full px-4 py-2 border rounded-lg font-semibold ${
                    isDark ? "bg-gray-700 border-gray-600 text-green-400" : "bg-white border-gray-300 text-green-600"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Reason
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => handleInputChange("reason", e.target.value)}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end items-center gap-6">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${buttonClass} ${hoverClass} text-white`}
            >
              <FaSave /> {loading ? "Saving..." : editflag ? "Update" : "Save"}
            </button>
          </div>

          <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorPaymentEntry;